import React, { useState, useEffect, useCallback } from 'react';
import './Maintenance.css';
import { formatDate } from '../utils/dates';
import { decodeText } from '../utils/maintenanceText';
import { ArchiveButton, ArchivedBadge, ShowArchivedToggle, RestoreButton } from './ArchiveControls';
import { DashboardSeeMore } from './usePagination';
import ManualBookingModal, { ContractorPickerBlock, DateStartDurationBlock } from './ManualBookingModal';
import { HeldJobBanner } from './HeldJobBanner';
import { ukWindowIso } from '../utils/ukTime';
import { ComplianceCloseoutPrompt } from './ComplianceCloseout';

function CompletionLine({ data }) {
  const ts = data.completed_at || data.completed_date;
  if (data.status !== 'completed' || !ts) return null;
  const d = new Date(ts);
  const when = isNaN(d) ? String(ts) : d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <div className="md-completed" data-testid="md-completed-line">
      <p style={{ margin: 0 }}>Completed {when}{data.completed_by ? ` by ${data.completed_by}` : ''}</p>
      {data.completion_note && <p style={{ margin: '2px 0 0' }} data-testid="md-completion-note">Note: {data.completion_note}</p>}
    </div>
  );
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = 'Bearer'; // Step 41 — auth via httpOnly cookie; this stub keeps existing headers harmless
const headers = { 'Authorization': API_KEY, 'Content-Type': 'application/json' };
const authOnly = { 'Authorization': API_KEY };

// 2026-06-07 — "Open" column removed. With the new managed_by_estateiq /
// managed_by_agent flow the tenant ack ALWAYS fires immediately on MR
// creation, so an MR transitions to `acknowledged` within the same
// request. Any straggler at `status=open` indicates a degraded send and
// is surfaced via the Acknowledged column (with a visual hint) until an
// operator can intervene. `closed` MRs are filtered out of the kanban
// entirely — they belong to history, not the work board.
// 2026-02 (iter 14, Issue 1a) — Kanban columns now include the
// intermediate `awaiting_tenant_availability` and terminal
// `agent_intervention` states so a card can never appear to "regress"
// to Acknowledged when the backend legitimately advances it. Before
// iter 14 the grouping loop silently fell rows without a matching
// column into Acknowledged, which caused escalated / stuck rows to
// look like they moved BACKWARDS in the flow.
//
// `agent_intervention` sits at the far right ("Needs Attention") so
// it's visually separated from the linear happy-path lanes and always
// obvious when a card requires a human decision.
// 2026-08-06 (Kanban consolidation) — Six display columns. The
// underlying `mr.status` values are unchanged (they drive real
// logic); we just group them for the board. Four Scheduling
// sub-states collapse into one column, and `agent_intervention`
// stops being a column — it surfaces as a red flag badge on the
// card with a "Show flagged only" toggle. Sub-state labels render
// inline on the card so nothing is hidden.
export const DISPLAY_COLUMNS = [
  { key: 'new', label: 'New',
    absorbs: ['acknowledged', 'open', 'stage1_running'] },
  { key: 'finding_contractor', label: 'Finding a Contractor',
    absorbs: ['contractors_notified', 'awaiting_quote_approval'] },
  { key: 'scheduling', label: 'Scheduling',
    absorbs: [
      'awaiting_tenant_availability',
      'pending_tenant_selection',
      'date_proposed',
      're_negotiating',
      'awaiting_tenant_confirmation',
    ] },
  { key: 'booked_in_progress', label: 'Booked',
    // 2026-08-12 (evening 9) — Quote Approval column removed
    // per user; quote state is now a per-card badge and a
    // header count filter. `in_progress` merged into Booked
    // because for a one-visit repair the two aren't
    // meaningfully different to an operator, and an empty
    // stage is worse than no stage. Mark-complete still gates
    // on state ∈ {contractor_assigned, in_progress} so
    // completion continues to work from either.
    absorbs: ['contractor_assigned', 'in_progress'] },
  { key: 'quote_rejected', label: 'Awaiting your call',
    // Distinct state after quote rejection. Unmistakable card:
    // operator rejected a price, automated flow has stopped,
    // job waiting on operator to have the commercial
    // conversation.
    absorbs: ['quote_rejected'] },
  { key: 'completed', label: 'Completed',
    absorbs: ['completed', 'cancelled'] },
];

// Underlying-status → sub-state label rendered on the card so the
// four Scheduling states are still distinguishable at a glance.
const SUB_STATE_LABELS = {
  acknowledged: null,
  open: 'Awaiting acknowledgement',
  stage1_running: null,
  contractors_notified: null,
  awaiting_quote_approval: 'Awaiting quote approval',
  awaiting_tenant_availability: 'Awaiting tenant',
  pending_tenant_selection: 'Tenant picking slot',
  date_proposed: 'Date proposed',
  re_negotiating: 'Renegotiating',
  awaiting_tenant_confirmation: 'Awaiting confirmation',
  contractor_assigned: 'Booked',
  in_progress: 'In progress',
  completed: null,
  agent_intervention: 'Needs attention',
  cancelled: 'Cancelled',
};

// Drag-drop target-column → representative status. Preserves the
// legacy override behaviour while keeping the underlying data
// model coherent.
const DISPLAY_COLUMN_DROP_STATUS = {
  new: 'acknowledged',
  finding_contractor: 'contractors_notified',
  scheduling: 'pending_tenant_selection',
  booked_in_progress: 'contractor_assigned',
  quote_rejected: 'quote_rejected',
  completed: 'completed',
};

// Underlying-status list for the MaintenanceDetail Status dropdown.
// Kept as the raw statuses (not the display columns) so the operator
// can move a card to any exact stage from the detail panel — the
// board is a grouped VIEW; this dropdown is the source-of-truth.
const STATUS_OPTIONS = [
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'contractors_notified', label: 'Contractors Notified' },
  { key: 'awaiting_tenant_availability', label: 'Awaiting Tenant' },
  { key: 'pending_tenant_selection', label: 'Tenant Picking Slot' },
  { key: 'date_proposed', label: 'Date Proposed' },
  { key: 're_negotiating', label: 'Re-negotiating' },
  { key: 'awaiting_tenant_confirmation', label: 'Awaiting Confirmation' },
  { key: 'contractor_assigned', label: 'Contractor Assigned' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'agent_intervention', label: 'Needs Attention' },
];

const COMPLIANCE_CERT_LABELS = {
  gas_safety: 'Gas Safety Certificate (CP12)',
  eicr: 'EICR',
  epc: 'EPC',
};

// X2 — compliance quote rejected: the flow stopped, the legal deadline didn't.
function ComplianceRejectedNotice({ r }) {
  const cert = COMPLIANCE_CERT_LABELS[r.compliance_doc_type] || 'certificate';
  const expiry = r.certificate_expiry_date
    ? new Date(r.certificate_expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  return (
    <div
      data-testid={`v2-compliance-rejected-${r.id}`}
      style={{
        fontSize: '0.72rem', background: '#fef2f2', border: '1px solid #f87171',
        borderRadius: 6, padding: '6px 10px', color: '#7f1d1d',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      <strong>Quote rejected — this renewal is now with you</strong>
      <span>Nothing further will be sent automatically on this job.</span>
      <span>
        The {cert} is still legally required{expiry ? ` (expires ${expiry})` : ''}. You'll
        still be alerted at 10 and 5 days before expiry and on the expiry day.
      </span>
      <span>
        Your options: get a quote from another contractor yourself, or arrange the
        visit and record it with "Set booking manually" in the job details.
      </span>
    </div>
  );
}

// X4 — compliance: several contractors quoted; the operator picks one.
function ComplianceQuotePicker({ r, quotes, busy, onApprove }) {
  return (
    <div data-testid={`v2-compliance-quotes-${r.id}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      {quotes.map((q) => (
        <div key={q.outreach_id} data-testid={`v2-compliance-quote-${q.outreach_id}`}
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem',
                   border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px' }}>
          <span style={{ flex: 1 }}>
            <strong>{q.contractor_company_name}</strong> — £{q.amount_gbp}
            {q.availability && <span style={{ color: '#64748b' }}> · {q.availability.slice(0, 80)}</span>}
          </span>
          <button type="button" disabled={busy}
            data-testid={`v2-approve-compliance-quote-${q.outreach_id}`}
            onClick={() => onApprove(q.outreach_id)}
            style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 4,
                     padding: '6px 10px', fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
            Approve
          </button>
        </div>
      ))}
    </div>
  );
}

function _subStateLabel(r) {
  if (r.status === 'awaiting_quote_approval' && !r.coord_quote_amount_gbp) {
    return 'Awaiting quote approval — no price given, chase';
  }
  return SUB_STATE_LABELS[r.status];
}

function _hasPendingQuote(r) {
  return Boolean(
    r.coord_quote_amount_gbp
    || r.coord_quote_received_at
    || r.coord_quote_summary,
  );
}

function _isFlagged(r) {
  // Red-flag surface: agent_intervention still exists as an
  // underlying status; also show flag when a contractor has
  // self-reported completion (Kanban 2b) and the agent hasn't
  // confirmed yet.
  return (
    r.status === 'agent_intervention'
    || (r.job_complete_reported_at && !r.job_complete_confirmed_at)
    || _hasUnmappedStatus(r)
  );
}

// 2026-08-06 (post-consolidation hotfix) — Every mr.status value the
// intake / coord layers can produce today. If a future status leaks
// through it will still render (via the fallback in the group loop)
// but a loud yellow "Unrecognised status" badge draws the eye so
// invisible cards can never happen. "Nothing may vanish" applies to
// the board itself, not just data.
const KNOWN_MR_STATUSES = new Set([
  'open', 'acknowledged', 'stage1_running',
  'contractors_notified', 'awaiting_tenant_availability',
  'pending_tenant_selection', 'date_proposed', 're_negotiating',
  'contractor_assigned', 'in_progress', 'completed',
  'agent_intervention', 'closed', 'awaiting_quote_approval', 'quote_rejected',
  'awaiting_tenant_confirmation', 'awaiting_operator_routing', 'cancelled',
]);
function _hasUnmappedStatus(r) {
  const s = r && r.status;
  if (!s) return true; // missing status is itself unrecognised
  return !KNOWN_MR_STATUSES.has(s);
}

export function _displayColumnFor(r) {
  // 2026-08-08 (post-3B5185) — Placeholder routing MRs render in
  // the New column with a distinct "Needs routing" badge (see
  // `_isAwaitingRoute` and the kanban card renderer). No new
  // column added — visibility comes from the badge + alert.
  if (r.status === 'awaiting_operator_routing') return 'new';
  if (r.status === 'completed') return 'completed';
  if (r.status === 'quote_rejected') return 'quote_rejected';
  if (r.status === 'in_progress') return 'booked_in_progress';
  if (r.status === 'contractor_assigned') {
    // 2026-08-12 (evening 9) — Quote Approval column removed;
    // ALL contractor_assigned cards live under Booked. Quote
    // state is a per-card badge, not a column.
    return 'booked_in_progress';
  }
  if (r.status === 'agent_intervention') {
    // Escalation lane: keep the card in the LAST meaningful stage
    // (the flag badge on the card marks it as needing attention).
    // In the absence of an anchor stage we fall back to Scheduling
    // — most escalations happen mid-scheduling.
    return 'scheduling';
  }
  for (const col of DISPLAY_COLUMNS) {
    if (col.absorbs.includes(r.status)) return col.key;
  }
  return null; // will surface a console warning; nothing loses.
}

const PRIORITY_COLORS = {
  emergency: { bg: '#fee2e2', color: '#991b1b', label: 'Emergency' },
  high: { bg: '#ffedd5', color: '#9a3412', label: 'High' },
  medium: { bg: '#fef9c3', color: '#854d0e', label: 'Medium' },
  low: { bg: '#f3f4f6', color: '#6b7280', label: 'Low' },
};

// 2026-06-04 (v2) — Routing panel for the maintenance detail view. Lets
// the agent see WHICH routing rule fired and override before the next
// outreach. Calls PATCH /api/maintenance/requests/:id/routing.
function RoutingPanel({ data, onChange }) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const flip = data.routed_to === 'specialist' ? 'handyman' : 'specialist';

  const overrideAndResend = async () => {
    if (!window.confirm(
      `Re-route to ${flip} and resend outreach emails?`
    )) return;
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/maintenance/requests/${data.id}/routing`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            routed_to: flip,
            reason: `Agent override — re-routed to ${flip}.`,
            resend: true,
          }),
        },
      );
      if (!res.ok) {
        const t = await res.text();
        setMsg(`Failed: ${t}`);
      } else {
        setMsg(`Re-routed to ${flip}. Outreach resent.`);
        if (onChange) onChange();
      }
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="md-routing-panel"
         style={{ marginTop: 12, padding: '0.75rem 1rem',
                  background: '#fff7ed', border: '1px solid #fed7aa',
                  borderRadius: 8 }}>
      <strong style={{ color: '#9a3412' }}>Routing decision</strong>
      <div style={{ marginTop: 4, fontSize: '0.9rem', color: '#0f172a' }}>
        <span style={{ fontWeight: 600,
                       color: data.routed_to === 'specialist' ? '#0891b2' : '#9a3412' }}>
          {data.routed_to === 'specialist' ? 'Specialist' : 'Handyman'}
        </span>
        {data.routing_reason ? ` — ${data.routing_reason}` : ''}
        {data.routing_overridden_by_agent && (
          <span style={{ marginLeft: 6, color: '#7c3aed', fontWeight: 600 }}>
            (overridden by agent)
          </span>
        )}
      </div>
      <button onClick={overrideAndResend} disabled={saving}
              data-testid="md-routing-override"
              style={{ marginTop: 8, background: '#ea580c', color: '#fff',
                       border: 'none', padding: '0.4rem 0.9rem',
                       borderRadius: 6,
                       cursor: saving ? 'not-allowed' : 'pointer',
                       fontSize: '0.85rem', fontWeight: 600 }}>
        {saving ? 'Sending…' : `Switch to ${flip} & resend`}
      </button>
      {msg && (
        <div data-testid="md-routing-msg"
             style={{ marginTop: 6, fontSize: '0.8rem', color: '#0f172a' }}>
          {msg}
        </div>
      )}
    </div>
  );
}

const CATEGORY_ICONS = {
  plumbing: '\u{1F6B0}', electrical: '\u26A1', heating: '\u{1F525}',
  structural: '\u{1F3D7}', appliance: '\u{1F3E0}', pest: '\u{1F41B}', other: '\u{1F527}',
};

const CATEGORIES = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'heating', label: 'Heating' },
  { value: 'structural', label: 'Structural' },
  { value: 'appliance', label: 'Appliance' },
  { value: 'pest', label: 'Pest' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'emergency', label: 'Emergency' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export function MaintenanceKanban({ tenancies, onOpenDetail, highlightMrId, onHighlightConsumed, dashboardMode = true }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  // 2026-02 FIX 2 — Show archived toggle (off by default).
  const [showArchivedMaint, setShowArchivedMaint] = useState(false);
  // 2026-02 FIX 1 — Per-column "+ See more" cursor (25-per-page).
  const [shownPerCol, setShownPerCol] = useState({});
  // 2026-08-06 (Kanban consolidation) — Flagged-only filter. Off
  // by default; when on we hide non-flagged cards so the board
  // becomes a triage view.
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // 2026-08-13 — Pending-quote filter. Turned on by clicking the
  // "Quotes awaiting approval (N)" pill on the Booked column
  // header. Filters the ENTIRE board to show only jobs with an
  // outstanding (not-yet-approved, not-yet-rejected) quote.
  // Filter, NOT a column — the cards stay in their normal
  // stage columns so their coordination state is still visible.
  const [pendingQuoteOnly, setPendingQuoteOnly] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/maintenance/requests`, { headers: authOnly });
      if (res.ok) setRequests(await res.json());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // 2026-06-09 — Deep-link from dashboard alerts. When the parent passes
  // a `highlightMrId`, scroll that card into view + briefly highlight it.
  // (Previously read window.location.hash, but the app has no /maintenance
  // route — the dashboard renders this component as an embedded section.)
  useEffect(() => {
    if (!highlightMrId || loading || requests.length === 0) return;
    const tid = setTimeout(() => {
      const card = document.querySelector(`[data-mr-id="${highlightMrId}"]`);
      if (!card) {
        // Card may be hidden under a status the kanban no longer renders
        // (e.g. closed). Just consume the highlight so we don't loop.
        if (onHighlightConsumed) onHighlightConsumed();
        return;
      }
      try { card.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      catch (_e) { card.scrollIntoView(); }
      card.classList.add('kanban-card-highlight');
      setTimeout(() => card.classList.remove('kanban-card-highlight'), 2500);
      if (onHighlightConsumed) onHighlightConsumed();
    }, 300);
    return () => clearTimeout(tid);
  }, [highlightMrId, loading, requests, onHighlightConsumed]);

  const handleDragStart = (e, item) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, targetDisplayKey) => {
    e.preventDefault();
    const currentDisplay = dragItem ? _displayColumnFor(dragItem) : null;
    if (!dragItem || currentDisplay === targetDisplayKey) {
      setDragItem(null);
      return;
    }
    // 2026-08-06 (Kanban consolidation) — Drag targets a DISPLAY
    // column; map to a representative underlying status so the
    // data model stays coherent.
    // 2026-08-12 (evening 9) — Quote Approval column removed;
    // approve/reject now happens via card actions instead of
    // by dragging.
    const targetStatus = DISPLAY_COLUMN_DROP_STATUS[targetDisplayKey];
    if (!targetStatus) { setDragItem(null); return; }
    // Optimistic update
    setRequests(prev => prev.map(r => r.id === dragItem.id ? { ...r, status: targetStatus } : r));
    setDragItem(null);
    try {
      await fetch(`${API_BASE_URL}/api/maintenance/requests/${dragItem.id}/status`, {
        method: 'PUT', headers,
        body: JSON.stringify({ status: targetStatus }),
      });
      window.dispatchEvent(new Event('eiq-alerts-refresh'));
    } catch {
      fetchRequests(); // Rollback
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  // 2026-08-08 (Bug B4) — Shared "open" selector.
  // Header total, kanban column filter, and the sidebar badge
  // must agree on which MRs count as "open". Definition:
  //   * `closed` status is never open.
  //   * `archived` is only open when the Show-archived toggle is on.
  //   * `completed` still shows on the board (it's a display
  //      column) but does NOT count as open work.
  const isOpenMr = (r) => {
    if (r.status === 'closed') return false;
    if (r.status === 'completed') return false;
    if (r.status === 'cancelled') return false;
    if (r.archived && !showArchivedMaint) return false;
    return true;
  };
  const openRequests = requests.filter(isOpenMr);

  const grouped = {};
  DISPLAY_COLUMNS.forEach(c => { grouped[c.key] = []; });
  requests.forEach(r => {
    if (r.status === 'closed') return;
    if (r.archived && !showArchivedMaint) return;
    if (flaggedOnly && !_isFlagged(r)) return;
    // 2026-08-13 — Pending-quote filter (kanban-wide).
    if (
      pendingQuoteOnly
      && !(
        _hasPendingQuote(r)
        && !r.coord_quote_approved_at
        && !r.coord_quote_rejected_at
      )
    ) return;
    const col = _displayColumnFor(r);
    if (col && grouped[col]) {
      grouped[col].push(r);
    } else {
      // 2026-08-06 (post-consolidation hotfix) — GUARANTEED FALLBACK.
      // If a future or legacy status doesn't match any absorbs list,
      // the card still renders in "New" and picks up an "Unmapped"
      // flag badge via `_isFlagged`. Nothing may vanish.
      console.warn(
        '[Maintenance] unmapped MR status',
        JSON.stringify({ status: r.status, ref: r.contractor_ref, id: r.id }),
        '— rendering in New with an Unmapped flag.',
      );
      grouped.new.push(r);
    }
  });

  return (
    <div className="maint-kanban-wrap" data-testid="maintenance-kanban">
      <div className="maint-kanban-header">
        <h3>Maintenance Requests</h3>
        <span className="maint-count" data-testid="maint-open-count">
          {openRequests.length} open
        </span>
        <ShowArchivedToggle value={showArchivedMaint} onChange={setShowArchivedMaint} testid="maint-show-archived" />
        <label
          data-testid="maint-flagged-only-toggle"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: '0.85rem', color: '#0f172a', cursor: 'pointer',
            marginLeft: 8,
          }}
        >
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={(e) => setFlaggedOnly(e.target.checked)}
            data-testid="maint-flagged-only-checkbox"
          />
          <span style={{ color: '#b91c1c', fontWeight: 600 }}>
            🚩 Flagged only
          </span>
        </label>
        <button className="btn-primary btn-sm" style={{ background: '#0073EA', color: '#fff' }} onClick={() => setShowForm(true)} data-testid="btn-new-maintenance">
          + Log Request
        </button>
      </div>

      {showForm && (
        <MaintenanceForm
          tenancies={tenancies}
          onClose={() => setShowForm(false)}
          onCreated={(mrId) => {
            setShowForm(false);
            fetchRequests();
            // Message 823 — navigate to the new card so the operator
            // can act on it immediately. Also honours the primed
            // sessionStorage payload (see MaintenanceDetail mount)
            // when the YES route hit an override-cancel or failure.
            if (mrId && onOpenDetail) onOpenDetail(mrId);
          }}
        />
      )}

      {loading ? (
        <p className="maint-loading">Loading maintenance requests...</p>
      ) : requests.length === 0 ? (
        <div
          data-testid="maintenance-empty-state"
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '2.25rem 1.75rem',
            textAlign: 'center',
            color: '#475569',
            margin: '0.75rem 0',
          }}
        >
          <p style={{ margin: 0, fontSize: '1.02rem', fontWeight: 600, color: '#0f172a' }}>
            No maintenance requests.
          </p>
          <p style={{ margin: '0.5rem auto 0', maxWidth: 640, fontSize: '0.92rem', lineHeight: 1.55, color: '#475569' }}>
            Maintenance requests are created automatically when a tenant reports an issue via WhatsApp or email.
            You can also log a request manually using the Log Request button above.
          </p>
        </div>
      ) : (
        <div className="maint-kanban-board" data-testid="kanban-board">
          {DISPLAY_COLUMNS.map(col => {
            const all = grouped[col.key] || [];
            const perCol = dashboardMode ? 6 : 25;
            const shown = shownPerCol[col.key] || perCol;
            const visible = all.slice(0, shown);
            const remaining = Math.max(0, all.length - shown);
            return (
            <div
              key={col.key}
              className={`maint-kanban-col${col.danger ? ' maint-kanban-col-danger' : ''}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, col.key)}
              data-testid={`kanban-col-${col.key}`}
            >
              <div className="kanban-col-header">
                <span>
                  {col.danger && (
                    <span
                      style={{
                        display: 'inline-block', width: 8, height: 8,
                        borderRadius: '50%', background: '#dc2626',
                        marginRight: 6, verticalAlign: 'middle',
                      }}
                      aria-hidden="true"
                    />
                  )}
                  {col.label}
                  {/* 2026-08-12 (evening 9) — Quotes-awaiting
                    count on the Booked column so operators can
                    see at a glance how many need attention
                    without scanning every card. */}
                  {col.key === 'booked_in_progress' && (() => {
                    // 2026-08-13 — Count across ALL requests (not
                    // just this column's filtered slice) so the
                    // pill stays visible after the filter is
                    // applied even if no Booked cards match.
                    const pendingQuotes = requests.filter(
                      r => r.status !== 'closed'
                        && (!r.archived || showArchivedMaint)
                        && _hasPendingQuote(r)
                        && !r.coord_quote_approved_at
                        && !r.coord_quote_rejected_at,
                    ).length;
                    if (pendingQuotes === 0 && !pendingQuoteOnly) {
                      return null;
                    }
                    return (
                      <button
                        type="button"
                        data-testid="v2-quotes-awaiting-count"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingQuoteOnly((v) => !v);
                        }}
                        title={
                          pendingQuoteOnly
                            ? 'Click to clear the filter'
                            : 'Click to filter the board to '
                              + 'jobs awaiting quote approval'
                        }
                        style={{
                          marginLeft: 8, padding: '1px 8px',
                          background: pendingQuoteOnly
                            ? '#fbbf24' : '#fef3c7',
                          border: '1px solid #fbbf24',
                          color: '#78350f',
                          borderRadius: 10,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Quotes awaiting approval ({pendingQuotes})
                        {pendingQuoteOnly ? ' ✕' : ''}
                      </button>
                    );
                  })()}
                </span>
                <span className="kanban-col-count">{all.length}</span>
              </div>
              <div className="kanban-col-body">
                {visible.map(r => {
                  const pc = PRIORITY_COLORS[r.priority] || PRIORITY_COLORS.low;
                  return (
                    <div
                      key={r.id}
                      className="kanban-card"
                      draggable
                      onDragStart={(e) => handleDragStart(e, r)}
                      onClick={() => onOpenDetail && onOpenDetail(r.id)}
                      data-testid={`kanban-card-${r.id}`}
                      data-mr-id={r.id}
                    >
                      <div className="kc-top">
                        <span className="kc-category">{CATEGORY_ICONS[r.category] || ''} {r.category}</span>
                        <span className="kc-priority" style={{ background: pc.bg, color: pc.color }}>{pc.label}</span>
                        {r.status === 'awaiting_operator_routing' && (
                          <span
                            data-testid={`kanban-card-needs-routing-${r.id}`}
                            title="Ambiguous inbound — classifier confidence below threshold. Assign to an existing job or confirm as new."
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              background: '#fff7ed',
                              color: '#9a3412',
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              marginLeft: 4,
                              border: '1px solid #fdba74',
                            }}>
                            NEEDS ROUTING
                          </span>
                        )}
                        {_isFlagged(r) && (
                          <span
                            data-testid={`kanban-card-flag-${r.id}`}
                            title={
                              _hasUnmappedStatus(r)
                                ? `Unrecognised status "${r.status || 'null'}" — please report`
                                : (r.status === 'agent_intervention'
                                    ? 'Needs attention — human decision required'
                                    : (r.job_complete_reported_at
                                        && !r.job_complete_confirmed_at
                                        ? 'Contractor reported job complete — confirm on the card'
                                        : 'Flagged'))
                            }
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              background: _hasUnmappedStatus(r) ? '#fef3c7' : '#fee2e2',
                              color: _hasUnmappedStatus(r) ? '#78350f' : '#991b1b',
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                            }}
                          >
                            {_hasUnmappedStatus(r)
                              ? `?? Unmapped: ${r.status || 'null'}`
                              : '🚩 Flagged'}
                          </span>
                        )}
                        {SUB_STATE_LABELS[r.status] && (
                          <span
                            data-testid={`kanban-card-substate-${r.id}`}
                            style={{
                              background: r.status === 're_negotiating'
                                ? '#fee2e2' : '#e0f2fe',
                              color: r.status === 're_negotiating'
                                ? '#991b1b' : '#075985',
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontSize: '0.68rem',
                              fontWeight: 600,
                            }}
                          >
                            {_subStateLabel(r)}
                          </span>
                        )}
                        {r.archived && <ArchivedBadge small />}
                      </div>
                      <div className="kc-address">{r.property_address || 'Unknown'}</div>
                      <div className="kc-desc">{decodeText(r.description || '').substring(0, 60)}{decodeText(r.description || '').length > 60 ? '...' : ''}</div>
                      <HeldJobBanner mr={r} onDone={fetchRequests} />
                      <div className="kc-footer">
                        <span className="kc-tenant">{r.tenant_name}</span>
                        <span className="kc-days">{r.days_open || 0}d</span>
                        {r.archived ? (
                          <RestoreButton kind="maintenance" id={r.id}
                                          onRestored={() => {
                                            setRequests(prev => prev.map(x => x.id === r.id ? { ...x, archived: false } : x));
                                            // Item 6 — refresh sidebar badge counts on restore.
                                            window.dispatchEvent(new Event('eiq-alerts-refresh'));
                                          }} />
                        ) : (
                          <span onClick={(e) => e.stopPropagation()}>
                            <ArchiveButton kind="maintenance" id={r.id} size="xs"
                                            testid={`kanban-archive-${r.id}`}
                                            onArchived={() => {
                                              setRequests(prev => prev.map(x => x.id === r.id ? { ...x, archived: true } : x));
                                              // Item 6 — refresh sidebar badge counts on archive.
                                              window.dispatchEvent(new Event('eiq-alerts-refresh'));
                                            }} />
                          </span>
                        )}
                      </div>
                      {/* 2026-06-04 — Contractor activity strip (Part 9). */}
                      {(r.contractor_ref || r.contractor_outreach_count) && (
                        <div className="kc-contractor"
                             data-testid={`kanban-card-contractors-${r.id}`}
                             style={{ marginTop: 6, paddingTop: 6,
                                      borderTop: '1px dashed #fed7aa',
                                      color: '#9a3412',
                                      fontSize: '0.72rem',
                                      display: 'flex',
                                      flexWrap: 'wrap', gap: 6 }}>
                          {r.contractor_ref && (
                            <span style={{ fontWeight: 700 }}>{r.contractor_ref}</span>
                          )}
                          <span>
                            {(r.contractor_outreach_count || 0)} contacted
                            {' · '}
                            {(r.contractor_replied_count || 0)} replied
                          </span>
                          {r.contractor_escalation_due_minutes != null && (
                            <span style={{ color: '#b45309' }}>
                              · esc. in {r.contractor_escalation_due_minutes}m
                            </span>
                          )}
                          {r.contractor_escalated_at && (
                            <span style={{ color: '#dc2626' }}>· escalated</span>
                          )}
                        </div>
                      )}
                      {/* 2026-06-04 (v2) — Routing rule that fired. */}
                      {r.routing_reason && (
                        <div data-testid={`kanban-card-routing-${r.id}`}
                             style={{ marginTop: 4, fontSize: '0.72rem',
                                      color: '#475569' }}>
                          <strong style={{ color: r.routed_to === 'specialist' ? '#0891b2' : '#9a3412' }}>
                            {r.routed_to === 'specialist' ? 'Specialist' : 'Handyman'}:
                          </strong>{' '}
                          {r.routing_reason}
                          {r.routing_overridden_by_agent && (
                            <span style={{ marginLeft: 4, color: '#7c3aed' }}>
                              (overridden)
                            </span>
                          )}
                        </div>
                      )}
                      {/* 2026-02 (v2 rebuild) — Stage 5 action buttons.
                          Rendered ONLY for coord_version === 'v2' MRs. v1
                          cards are unchanged. */}
                      {r.is_compliance === true && (
                        <ComplianceCloseoutPrompt r={r} onChanged={(patch) => setRequests(prev =>
                          prev.map(x => x.id === r.id ? { ...x, ...patch } : x))} />
                      )}
                      {r.coord_version === 'v2' && (
                        <V2StageButtons
                          request={r}
                          onUpdated={(newStatus, patch) => setRequests(prev =>
                            prev.map(x => x.id === r.id
                              ? { ...x, status: newStatus,
                                  coord_state: newStatus,
                                  ...(patch || {}) }
                              : x)
                          )}
                          onOpenDraft={(mrId) => {
                            // 2026-02 (post-deploy batch #3) — when
                            // a Quote Approve queued a draft, take
                            // the operator straight to the pending
                            // section AND focus the Send button
                            // (the `focus=send` query param is
                            // consumed by PendingMessagesSection
                            // when the modal mounts).
                            //
                            // ORDER MATTERS: URL params must be
                            // set BEFORE `onOpenDetail` fires,
                            // because `MaintenanceDetail` mounts
                            // synchronously and its child
                            // `PendingMessagesSection` reads the
                            // params on its first render. If the
                            // detail opener is ever changed to a
                            // react-router navigation, this
                            // history.replaceState will be
                            // clobbered — add a router-safe
                            // channel then.
                            try {
                              const url = new URL(window.location.href);
                              url.searchParams.set('pane', 'pending');
                              url.searchParams.set('focus', 'send');
                              window.history.replaceState(
                                null, '', url.toString(),
                              );
                            } catch (_e) { /* non-fatal */ }
                            if (onOpenDetail) onOpenDetail(mrId);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                {all.length === 0 && <div className="kanban-empty">No items</div>}
                {dashboardMode ? (
                  all.length > 0 && (
                    <div style={{ width: '100%', marginTop: 8 }}>
                      <DashboardSeeMore to="/maintenance-all"
                                          totalRemaining={remaining}
                                          totalCount={all.length}
                                          testid={`kanban-${col.key}`} />
                    </div>
                  )
                ) : (
                  remaining > 0 && (
                    <button
                      type="button"
                      onClick={() => setShownPerCol(prev => ({ ...prev, [col.key]: shown + 25 }))}
                      data-testid={`see-more-kanban-${col.key}`}
                      style={{ width: '100%', marginTop: 8,
                               background: '#f1f5f9', color: '#0f172a',
                               border: '1px solid #e2e8f0', borderRadius: 6,
                               padding: '0.45rem 0.75rem', fontWeight: 600,
                               fontSize: '0.78rem', cursor: 'pointer' }}>
                      + See more · {Math.min(remaining, 25)} of {remaining} remaining
                    </button>
                  )
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export function MaintenanceForm({ tenancies, onClose, onCreated, defaultTenancyId }) {
  const [form, setForm] = useState({
    tenancy_id: defaultTenancyId || '',
    // Message 826 — default is '' (empty) meaning "classify
    // automatically": the backend runs the same `_extract_intake`
    // extractor the email path uses when it sees an empty/'other'
    // category. Operator can pick a concrete category to override.
    category: '',
    priority: '',
    description: '',
    agent_notes: '',
  });
  const [aiPriority, setAiPriority] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);

  // Message 823 — "Has this already been arranged?" branch. Default
  // is NO (send the tenant an acknowledgement and start contractor
  // outreach — exactly like an emailed report). YES reveals the
  // contractor picker + date/start/duration and books through the
  // existing /manual-book endpoint after create. Explicit rather
  // than inferred — leaving a field blank must never silently
  // change which emails go out.
  const [alreadyArranged, setAlreadyArranged] = useState(false);
  const [contractorId, setContractorId] = useState('');
  const [bookDate, setBookDate] = useState('');
  const [bookStart, setBookStart] = useState('10:00');
  const [bookDuration, setBookDuration] = useState(60);

  const [formError, setFormError] = useState('');
  const [override, setOverride] = useState(null);       // {kind, ...}
  const [createdMrId, setCreatedMrId] = useState(null); // set after step 1
  const [orphanState, setOrphanState] = useState(null); // {message} for step-2 hard failure

  const apiKey = localStorage.getItem('apiKey') || '';

  // Prime the manual-booking modal on the card with the slot the
  // operator already chose, so on override-cancel or step-2 failure
  // they land on the card with the modal open and their inputs
  // preserved. Consumed once by MaintenanceDetail on mount.
  const _primeManualBookingModal = (mrId, priorErrorMessage) => {
    try {
      const payload = {
        contractorId,
        date: bookDate,
        startTime: bookStart,
        duration: bookDuration,
        priorErrorMessage: priorErrorMessage || '',
      };
      sessionStorage.setItem(
        `maintPrimedManualBooking_${mrId}`,
        JSON.stringify(payload),
      );
    } catch { /* sessionStorage full/blocked — non-fatal */ }
  };

  const _postManualBook = async (mrId, opts = {}) => {
    const win = ukWindowIso(bookDate, bookStart, bookDuration);
    const body = {
      contractor_id: contractorId,
      scheduled_start: win.start,
      scheduled_end: win.end,
      allow_outside_social_hours: !!opts.allowSocial,
      allow_past_time: !!opts.allowPast,
    };
    const res = await fetch(`${API_BASE_URL}/api/maintenance/${mrId}/manual-book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  };

  const submitYesRoute = async () => {
    // Local validation before we create anything.
    if (!contractorId) { setFormError('Pick a contractor (or create one) before booking.'); return; }
    if (!bookDate) { setFormError('Pick a date for the booking.'); return; }
    setFormError('');

    // Step 1 — create MR with already_arranged=true. Skips ack + outreach.
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/maintenance/create`, {
        method: 'POST', headers,
        body: JSON.stringify({
          tenancy_id: form.tenancy_id,
          category: form.category,
          priority: form.priority || undefined,
          description: form.description,
          agent_notes: form.agent_notes || undefined,
          already_arranged: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Surface tenant_email_missing inline; other errors as plain detail.
        const detail = data.detail;
        if (detail && typeof detail === 'object' && detail.code === 'tenant_email_missing') {
          setFormError(detail.message);
        } else {
          setFormError(typeof detail === 'string' ? detail : 'Failed to create the job.');
        }
        return;
      }
      const mrId = data.id;
      setCreatedMrId(mrId);

      // Photo upload piggy-backs on step 1 (same as NO route).
      if (photos.length > 0) {
        const fd = new FormData();
        photos.forEach(f => fd.append('files', f));
        await fetch(`${API_BASE_URL}/api/maintenance/requests/${mrId}/photos`, {
          method: 'POST', headers: authOnly, body: fd,
        });
      }

      // Step 2 — manual-book against the new MR.
      const r2 = await _postManualBook(mrId);
      if (r2.ok && r2.data.override_available) {
        // Show inline override panel; operator confirms or cancels.
        setOverride(r2.data);
        return;
      }
      if (!r2.ok) {
        // Hard failure — MR exists, no booking, no emails. Named-fact
        // orphan message + "Open the card" primary action.
        _primeManualBookingModal(
          mrId,
          'The job was created but the booking didn\'t go through. Neither party has been contacted.',
        );
        setOrphanState({
          message: 'The job was created but the booking didn\'t go through. Neither party has been contacted.',
        });
        return;
      }
      // Booked successfully.
      if (onCreated) onCreated(mrId);
    } catch (err) {
      setFormError(err.message || 'Something went wrong. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const submitNoRoute = async () => {
    if (!form.tenancy_id || !form.description) return;
    setFormError('');
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/maintenance/create`, {
        method: 'POST', headers,
        body: JSON.stringify({
          tenancy_id: form.tenancy_id,
          category: form.category,
          priority: form.priority || undefined,
          description: form.description,
          agent_notes: form.agent_notes || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAiPriority(data.ai_priority);
        if (photos.length > 0 && data.id) {
          const fd = new FormData();
          photos.forEach(f => fd.append('files', f));
          await fetch(`${API_BASE_URL}/api/maintenance/requests/${data.id}/photos`, {
            method: 'POST', headers: authOnly, body: fd,
          });
        }
        if (onCreated) onCreated(data.id);
      } else {
        const detail = data.detail;
        if (detail && typeof detail === 'object' && detail.code === 'tenant_email_missing') {
          setFormError(detail.message);
        } else {
          setFormError(typeof detail === 'string' ? detail : 'Failed to create');
        }
      }
    } catch (err) { setFormError(err.message); }
    finally { setSaving(false); }
  };

  const handleSubmit = () => alreadyArranged ? submitYesRoute() : submitNoRoute();

  const confirmOverride = async () => {
    if (!createdMrId || !override) return;
    setSaving(true);
    setFormError('');
    try {
      const r = await _postManualBook(createdMrId, {
        allowSocial: override.kind === 'social_hours',
        allowPast: override.kind === 'past_time',
      });
      if (!r.ok) {
        _primeManualBookingModal(
          createdMrId,
          'The job was created but the booking didn\'t go through. Neither party has been contacted.',
        );
        setOverride(null);
        setOrphanState({
          message: 'The job was created but the booking didn\'t go through. Neither party has been contacted.',
        });
        return;
      }
      if (onCreated) onCreated(createdMrId);
    } finally { setSaving(false); }
  };

  const cancelOverride = () => {
    // Refuse-to-close-with-orphan: MR exists, no booking, no emails.
    // Land the operator on the card with the modal primed with the
    // slot they chose so they can complete it in one click. Never
    // silently return to the board.
    if (createdMrId) {
      _primeManualBookingModal(
        createdMrId,
        override && override.kind === 'social_hours'
          ? 'Outside working hours — book anyway when you\'re ready, or pick a different time.'
          : 'Time in the past — book anyway when you\'re ready, or pick a different time.',
      );
      setOverride(null);
      if (onCreated) onCreated(createdMrId);
    } else {
      setOverride(null);
    }
  };

  const cancelForm = () => {
    // Refuse-to-close-with-orphan guard when a YES-route MR was
    // created but never booked. Never drop them back to an empty
    // board thinking nothing happened.
    if (createdMrId) {
      const proceed = window.confirm(
        'You\'ll leave a created-but-unbooked job on the board. ' +
        'Open the card and finish booking?',
      );
      if (proceed) {
        _primeManualBookingModal(
          createdMrId,
          'The job was created but not yet booked. Neither party has been contacted.',
        );
        if (onCreated) onCreated(createdMrId);
      } else {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // --- render helpers ---
  if (orphanState && createdMrId) {
    return (
      <div className="maint-form-overlay" data-testid="maintenance-form">
        <div className="maint-form-panel">
          <div className="mf-header">
            <h3>Booking didn't go through</h3>
          </div>
          <div className="mf-body">
            <div data-testid="mf-orphan-message"
                 style={{ padding: '.75rem 1rem', background: '#fff7e6',
                          border: '1px solid #f0c883', borderRadius: 6,
                          fontSize: '.95em', lineHeight: 1.5 }}>
              {orphanState.message}
            </div>
          </div>
          <div className="mf-footer">
            <button
              className="btn-primary"
              style={{ background: '#0073EA', color: '#fff' }}
              onClick={() => { if (onCreated) onCreated(createdMrId); }}
              data-testid="mf-orphan-open-card"
            >
              Open the card and finish booking
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (override) {
    const heading = override.kind === 'social_hours'
      ? 'Outside working hours'
      : 'Time in the past';
    return (
      <div className="maint-form-overlay" data-testid="maintenance-form">
        <div className="maint-form-panel">
          <div className="mf-header">
            <h3 style={{ color: '#b57000' }} data-testid="mf-override-heading">{heading}</h3>
          </div>
          <div className="mf-body">
            <p data-testid="mf-override-message" style={{ fontSize: '.95em', lineHeight: 1.5 }}>
              {override.kind === 'social_hours'
                ? 'This is outside working hours (08:00-18:00). Both the contractor and the tenant will be sent a confirmation for this time.'
                : 'This time has already passed. Both the contractor and the tenant will be sent a confirmation for it.'}
            </p>
          </div>
          <div className="mf-footer">
            <button
              className="btn-primary"
              style={{ background: '#0073EA', color: '#fff' }}
              onClick={confirmOverride}
              disabled={saving}
              data-testid="mf-override-confirm"
            >
              {saving ? 'Booking...' : 'Book anyway'}
            </button>
            <button
              className="btn-secondary"
              onClick={cancelOverride}
              data-testid="mf-override-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="maint-form-overlay" data-testid="maintenance-form">
      <div className="maint-form-panel">
        <div className="mf-header">
          <h3>Log Maintenance Request</h3>
          <button className="btn-close" onClick={cancelForm}>&times;</button>
        </div>
        <div className="mf-body">
          <div className="mf-field">
            <label>Tenancy / Property</label>
            <select value={form.tenancy_id} onChange={e => setForm(f => ({ ...f, tenancy_id: e.target.value }))} data-testid="maint-tenancy-select">
              <option value="">-- Select --</option>
              {(tenancies || []).map(t => (
                <option key={t.id || t._id} value={t.id || t._id}>
                  {t.tenant_name || t.name} — {t.property_address || t.property || ''}
                </option>
              ))}
            </select>
          </div>
          <div className="mf-row">
            <div className="mf-field">
              <label>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="maint-category-select">
                {/* Message 826 — default 'Classify automatically' runs
                    the same LLM extractor the email path uses. Picking
                    a concrete category below is an explicit override. */}
                <option value="">Classify automatically</option>
                {CATEGORIES.filter(c => c.value !== 'other').map(c =>
                  <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="mf-field">
              <label>Priority {aiPriority && <span className="mf-ai-hint">(AI suggests: {aiPriority})</span>}</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} data-testid="maint-priority-select">
                <option value="">AI will classify</option>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="mf-field">
            <label>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} placeholder="Describe the issue..." data-testid="maint-description" />
          </div>
          <div className="mf-field">
            <label>Photos (optional)</label>
            <input type="file" accept="image/*" multiple onChange={e => setPhotos([...e.target.files])} data-testid="maint-photos" />
          </div>
          <div className="mf-field">
            <label>Agent Notes (optional)</label>
            <textarea value={form.agent_notes} onChange={e => setForm(f => ({ ...f, agent_notes: e.target.value }))} rows={2} placeholder="Internal notes..." data-testid="maint-notes" />
          </div>

          {/* Message 823 — Explicit YES/NO choice. Leaving a field
              blank must never silently change which emails fire. */}
          <div className="mf-field" style={{
            marginTop: '.5rem', padding: '.75rem 1rem',
            background: '#f7f8fa', border: '1px solid #e2e6ee',
            borderRadius: 6,
          }}>
            <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: 600 }}>
              Has this already been arranged?
            </label>
            <div style={{ display: 'flex', gap: '1.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontWeight: 400 }}>
                <input
                  type="radio"
                  checked={!alreadyArranged}
                  onChange={() => setAlreadyArranged(false)}
                  data-testid="maint-arranged-no"
                />
                No — start contractor outreach
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontWeight: 400 }}>
                <input
                  type="radio"
                  checked={alreadyArranged}
                  onChange={() => setAlreadyArranged(true)}
                  data-testid="maint-arranged-yes"
                />
                Yes — record the booking now
              </label>
            </div>
            <div style={{ fontSize: '.85em', color: '#666', marginTop: '.4rem' }}>
              {alreadyArranged
                ? 'Sends both parties the standard booking confirmation with a calendar invite. No outreach email, no availability request.'
                : 'Sends the tenant an acknowledgement and reaches out to a contractor.'}
            </div>
          </div>

          {alreadyArranged && (
            <>
              <ContractorPickerBlock
                apiKey={apiKey}
                contractorId={contractorId}
                onContractorIdChange={setContractorId}
                onError={setFormError}
                testIdPrefix="mf-yes"
              />
              <DateStartDurationBlock
                date={bookDate} setDate={setBookDate}
                startTime={bookStart} setStartTime={setBookStart}
                duration={bookDuration} setDuration={setBookDuration}
                testIdPrefix="mf-yes"
              />
            </>
          )}

          {formError && (
            <div data-testid="mf-form-error" style={{ color: '#c00', marginTop: '.75rem', fontSize: '.9em' }}>
              {formError}
            </div>
          )}
        </div>
        <div className="mf-footer">
          <button className="btn-secondary" onClick={cancelForm}>Cancel</button>
          <button
            className="btn-primary"
            style={{ background: '#0073EA', color: '#fff' }}
            onClick={handleSubmit}
            disabled={saving
                      || !form.tenancy_id
                      || !form.description
                      || (alreadyArranged && (!contractorId || !bookDate))}
            data-testid="btn-submit-maintenance"
          >
            {saving
              ? (alreadyArranged ? 'Creating & booking...' : 'Creating...')
              : (alreadyArranged ? 'Create & Book' : 'Create Request')}
          </button>
        </div>
      </div>
    </div>
  );
}


export function MaintenanceThread({ requestId, headers }) {
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    if (!requestId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/maintenance/${requestId}/thread`,
          { headers },
        );
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setMessages(d.messages || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [requestId, headers]);

  if (messages === null) return null;
  if (messages.length === 0) {
    return (
      <div data-testid="md-thread-empty"
           style={{ margin: '0.75rem 0', padding: '0.6rem 0.8rem',
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: 6, fontSize: '0.85rem', color: '#64748b' }}>
        No messages yet on this maintenance request.
      </div>
    );
  }

  return (
    <div className="md-thread" data-testid="md-conversation-thread"
         style={{ margin: '0.75rem 0' }}>
      <strong style={{ fontSize: '0.95rem' }}>
        Full Conversation ({messages.length})
      </strong>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column',
                    gap: 6, maxHeight: 320, overflowY: 'auto' }}>
        {messages.map((m, i) => (
          <div key={i} data-testid={`md-thread-msg-${i}`}
               style={{ padding: '0.5rem 0.7rem', borderRadius: 6,
                        background: m.direction === 'inbound' ? '#eff6ff' : m.kind === 'completion' ? '#f0fdf4' : '#f1f5f9',
                        border: '1px solid',
                        borderColor: m.direction === 'inbound' ? '#bfdbfe' : m.kind === 'completion' ? '#bbf7d0' : '#e2e8f0',
                        fontSize: '0.82rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          fontWeight: 600, marginBottom: 3, color: '#334155' }}>
              <span>
                {m.kind === 'completion' ? '✓ Job record' : m.direction === 'inbound' ? '← Inbound' : '→ Outbound'}
                {m.contractor_email
                  ? ` · contractor ${m.contractor_email}`
                  : ''}
              </span>
              {m.delivery_status === 'not_sent_agent_manages' && (
                <span data-testid={`thread-not-sent-${m.id || m.sent_at}`}
                      style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1',
                               borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>
                  Not sent (Agent Manages)
                </span>
              )}
              <span style={{ color: '#64748b', fontWeight: 400,
                             fontSize: '0.75rem' }}>
                {m.sent_at
                  ? new Date(m.sent_at).toLocaleString('en-GB', {
                      day: 'numeric', month: 'short', hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </span>
            </div>
            {/* 2026-02 (Bug 3 chip) — AI verdict pill next to
                inbound messages. Spot-check signal for the
                operator; shows what the classifier picked without
                needing to dig into logs. */}
            {m.direction === 'inbound' && m.classifier_verdict ? (
              <div style={{ marginBottom: 4 }}
                   data-testid={`md-thread-msg-${i}-verdict`}>
                <span style={{
                  display: 'inline-block',
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 999,
                  letterSpacing: '0.02em',
                  ...(
                    // Colour by verdict semantics — question =
                    // amber (needs attention), acknowledgement =
                    // grey (silent), slot/decline = blue.
                    m.classifier_verdict === 'question' ||
                    m.classifier_verdict === 'unclear'
                      ? { background: '#fef3c7', color: '#92400e',
                          border: '1px solid #fcd34d' }
                    : m.classifier_verdict === 'acknowledgement'
                      ? { background: '#f1f5f9', color: '#475569',
                          border: '1px solid #cbd5e1' }
                    : m.classifier_verdict === 'slot_decline'
                      ? { background: '#fee2e2', color: '#991b1b',
                          border: '1px solid #fca5a5' }
                    : { background: '#dbeafe', color: '#1e40af',
                        border: '1px solid #93c5fd' }
                  )
                }}>
                  AI verdict: {m.classifier_verdict}
                  {m.classifier_topic
                    ? ` · ${m.classifier_topic}`
                    : ''}
                  {typeof m.classifier_confidence === 'number'
                    ? ` · ${m.classifier_confidence.toFixed(2)}`
                    : ''}
                </span>
              </div>
            ) : null}
            {m.subject && (
              <div style={{ fontStyle: 'italic', marginBottom: 3,
                            color: '#475569' }}>
                {decodeText(m.subject)}
              </div>
            )}
            {/* Item A (2026-02) — Central decode ensures every
                thread message renders clean plain text with no
                raw `&nbsp;`. `dangerouslySetInnerHTML` replaced
                with a text render so entities never re-appear
                on the wire. */}
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {decodeText(m.body_html).substring(0, 800)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// 2026-02 (iter 17, Issue 3) — Pending Messages section.
// Lists inbound messages that the coordinator flagged as candidates
// to forward to the tenant/contractor. Each row exposes
// Approve-and-Send, Edit, Dismiss. Deep-linked from the dashboard
// notification via `?pane=pending` (auto-scroll on mount).
// 2026-02 (Bug 2 refix v5 → Item A refactor) — Text-decoding is
// now centralised in `../utils/maintenanceText`. The old local
// `_decodeInboundText` has been replaced by the shared
// `decodeText` import above so the card, card-edit, Pending
// Messages, alerts, and any other view all agree on rendering.

function PendingMessagesSection({ requestId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [busyId, setBusyId] = useState(null);
  const sectionRef = React.useRef(null);
  // 2026-02 (post-deploy batch #3) — On deep-link with
  // `?focus=send`, capture the ref of the first pending row's
  // Approve & Send button so we can focus + highlight it after
  // the list loads. Prevents the "dead-end" perception where the
  // required next step is buried below the fold.
  const firstSendBtnRef = React.useRef(null);

  const load = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/maintenance/${requestId}/pending-messages`,
        { credentials: 'include' },
      );
      if (res.ok) {
        const d = await res.json();
        setItems((d.items || []).filter(i => i.status === 'pending'));
      } else {
        // Bug 2 refix v4 — surface read errors instead of silently
        // dropping them. The dead-end UX was caused by a
        // DataIsolationError from the /pending-messages endpoint
        // being swallowed here, leaving the section empty with no
        // signal to the operator or the console.
        let detail = '';
        try { detail = (await res.text()).slice(0, 400); } catch (_e) { /* non-fatal */ }
        console.error(
          '[PendingMessagesSection] load failed',
          res.status, res.statusText, detail,
        );
      }
    } catch (e) {
      console.error('[PendingMessagesSection] load threw', e);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { if (requestId) load(); }, [requestId]);

  // Deep-link scroll: if URL carries ?pane=pending, scroll section
  // into view when the panel opens. If it also carries
  // `?focus=send`, focus the first Approve & Send button and
  // briefly highlight it so the operator's eye lands on it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pane') === 'pending' && sectionRef.current) {
      setTimeout(() => sectionRef.current?.scrollIntoView(
        { behavior: 'smooth', block: 'start' }
      ), 200);
    }
    if (params.get('focus') === 'send' && firstSendBtnRef.current) {
      setTimeout(() => {
        try {
          firstSendBtnRef.current?.focus({ preventScroll: false });
          firstSendBtnRef.current?.classList.add(
            'pending-send-flash',
          );
          setTimeout(() => firstSendBtnRef.current
            ?.classList.remove('pending-send-flash'), 2500);
        } catch (_e) { /* non-fatal */ }
        // One-shot: strip the param so a later re-render doesn't
        // steal focus from operator input.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('focus');
          window.history.replaceState(null, '', url.toString());
        } catch (_e) { /* non-fatal */ }
      }, 300);
    }
  }, [items.length]);

  const doApprove = async (msg) => {
    setBusyId(msg.id);
    try {
      const body = editingId === msg.id
        ? { draft_body: editDraft } : {};
      const res = await fetch(
        `${API_BASE_URL}/api/maintenance/${requestId}/pending-messages/${msg.id}/approve`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (res.ok) { setEditingId(null); setEditDraft(''); await load(); }
    } finally { setBusyId(null); }
  };

  const doDismiss = async (msg) => {
    setBusyId(msg.id);
    try {
      await fetch(
        `${API_BASE_URL}/api/maintenance/${requestId}/pending-messages/${msg.id}/dismiss`,
        { method: 'POST', credentials: 'include' },
      );
      await load();
    } finally { setBusyId(null); }
  };

  const startEdit = (msg) => {
    setEditingId(msg.id);
    // Bug 3 refix — the CARD view previously read `msg.draft`
    // directly, while the global Pending Messages view fell back
    // to `original_body` via the union helper's
    // `_preview = _draft if _draft else _orig` rule. Same DB
    // row, two different starting textareas. Mirror the union
    // helper's fallback here so the operator always has
    // something to edit, and the two views agree.
    // Item A (2026-02) — Also run through `decodeText` as
    // defence-in-depth for rows persisted before the write-side
    // decoder was in place.
    const startingText = (msg.draft && msg.draft.trim())
      ? msg.draft
      : (msg.original_body || '');
    setEditDraft(decodeText(startingText));
  };

  const saveEdit = async (msg) => {
    setBusyId(msg.id);
    try {
      await fetch(
        `${API_BASE_URL}/api/maintenance/${requestId}/pending-messages/${msg.id}/edit`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ draft_body: editDraft }),
        },
      );
      setEditingId(null);
      setEditDraft('');
      await load();
    } finally { setBusyId(null); }
  };

  if (loading) return null;
  if (!items.length) return null;

  return (
    <div
      ref={sectionRef}
      className="md-pending-messages"
      data-testid="pending-messages-section"
      style={{ margin: '1rem 0', padding: '0.75rem 1rem',
                background: '#fffbeb', border: '1px solid #fcd34d',
                borderRadius: 8 }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8, color: '#92400e',
                     fontSize: '0.95rem' }}>
        Pending messages — review and forward
      </div>
      {items.map((m, _idx) => (
        <div key={m.id}
              data-testid={`pending-msg-${m.id}`}
              style={{ background: '#fff', borderRadius: 6,
                       padding: '0.6rem 0.8rem', marginBottom: 8,
                       border: '1px solid #fde68a' }}>
          {/* MR-V2-7D0B5E (2026-08-11) — Header shows sender +
              recipient. Second row shows subject + timestamp so
              operators can judge each draft's appropriateness
              against the ORIGINAL inbound, not just the body. */}
          <div style={{ fontSize: '0.78rem', color: '#78350f',
                        marginBottom: 4 }}>
            From <strong>{m.original_from || 'unknown'}</strong>{' '}
            → forward to <strong>{m.recipient_email || 'no recipient'}</strong>{' '}
            ({m.recipient_role})
          </div>
          <div style={{ fontSize: '0.75rem', color: '#92400e',
                        marginBottom: 6,
                        display: 'flex', gap: '0.9rem',
                        flexWrap: 'wrap' }}
                data-testid={`pending-msg-meta-${m.id}`}>
            {m.original_subject ? (
              <span>
                <strong>Subject:</strong>{' '}
                {decodeText(m.original_subject)}
              </span>
            ) : null}
            {m.created_at ? (
              <span data-testid={`pending-msg-ts-${m.id}`}>
                <strong>Received:</strong>{' '}
                {(() => {
                  try {
                    return new Date(m.created_at).toLocaleString(
                      'en-GB', {
                        day: '2-digit', month: 'short',
                        hour: '2-digit', minute: '2-digit',
                      },
                    );
                  } catch (e) {
                    return m.created_at;
                  }
                })()}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#44403c',
                        whiteSpace: 'pre-wrap', marginBottom: 6,
                        background: '#fef3c7', padding: '0.4rem 0.6rem',
                        borderRadius: 4 }}>
            <strong>Original:</strong> {decodeText(m.original_body)}
          </div>
          {/* 2026-02 (Bug 3) — Operator-only "card_notes" surfaces
              private context (e.g. access instructions) that we
              deliberately DIDN'T include in the auto-draft. Blue
              info-callout style, always visible when present. */}
          {m.card_notes ? (
            <div data-testid={`pending-msg-notes-${m.id}`}
                 style={{ fontSize: '0.8rem', color: '#1e3a8a',
                          whiteSpace: 'pre-wrap', marginBottom: 6,
                          background: '#dbeafe', padding: '0.4rem 0.6rem',
                          borderRadius: 4,
                          border: '1px solid #93c5fd' }}>
              <strong>Operator notes (not sent):</strong>{' '}
              {decodeText(m.card_notes)}
            </div>
          ) : null}
          {editingId === m.id ? (
            <textarea
              data-testid={`pending-msg-draft-${m.id}`}
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              rows={5}
              style={{ width: '100%', fontSize: '0.85rem',
                       padding: '0.4rem', borderRadius: 4,
                       border: '1px solid #cbd5e1', marginBottom: 6 }}
            />
          ) : (
            // 2026-02 (Bug 3 refix) — Match the Pending Messages
            // union helper's fallback so the CARD view and the
            // global page show the same content: draft when
            // present, original inbound body otherwise. Prevents
            // the "Pending Messages says one thing, card says
            // No draft yet" mismatch the operator reported.
            (m.draft && m.draft.trim()) ? (
              <div style={{ fontSize: '0.85rem', color: '#1e293b',
                            whiteSpace: 'pre-wrap', marginBottom: 6 }}>
                <strong>Draft:</strong> {decodeText(m.draft)}
              </div>
            ) : (m.original_body && m.original_body.trim()) ? (
              <div style={{ fontSize: '0.85rem', color: '#334155',
                            whiteSpace: 'pre-wrap', marginBottom: 6 }}>
                <strong>Draft:</strong>{' '}
                <span style={{ fontStyle: 'italic', color: '#78716c' }}>
                  (no auto-draft — click Edit to write one; the
                  original inbound is preloaded)
                </span>
              </div>
            ) : (
              <div style={{ fontSize: '0.75rem', color: '#78716c',
                            fontStyle: 'italic', marginBottom: 6 }}>
                No draft yet — click Edit to write one before
                forwarding, or Approve to send the original
                message as-is.
              </div>
            )
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {editingId === m.id ? (
              <>
                <button
                  onClick={() => saveEdit(m)} disabled={busyId === m.id}
                  data-testid={`pending-msg-save-${m.id}`}
                  style={{ background: '#059669', color: '#fff',
                            border: 'none', padding: '0.35rem 0.75rem',
                            borderRadius: 4, cursor: 'pointer',
                            fontSize: '0.8rem' }}>
                  Save Draft
                </button>
                <button
                  onClick={() => { setEditingId(null); setEditDraft(''); }}
                  data-testid={`pending-msg-cancel-${m.id}`}
                  style={{ background: '#e2e8f0', color: '#334155',
                            border: 'none', padding: '0.35rem 0.75rem',
                            borderRadius: 4, cursor: 'pointer',
                            fontSize: '0.8rem' }}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => startEdit(m)} disabled={busyId === m.id}
                data-testid={`pending-msg-edit-${m.id}`}
                style={{ background: '#0ea5e9', color: '#fff',
                          border: 'none', padding: '0.35rem 0.75rem',
                          borderRadius: 4, cursor: 'pointer',
                          fontSize: '0.8rem' }}>
                Edit
              </button>
            )}
            <button
              ref={_idx === 0 ? firstSendBtnRef : null}
              onClick={() => doApprove(m)} disabled={busyId === m.id}
              data-testid={`pending-msg-approve-${m.id}`}
              style={{ background: '#16a34a', color: '#fff',
                        border: 'none', padding: '0.35rem 0.75rem',
                        borderRadius: 4, cursor: 'pointer',
                        fontSize: '0.8rem', fontWeight: 600 }}>
              Approve &amp; Send
            </button>
            <button
              onClick={() => doDismiss(m)} disabled={busyId === m.id}
              data-testid={`pending-msg-dismiss-${m.id}`}
              style={{ background: '#dc2626', color: '#fff',
                        border: 'none', padding: '0.35rem 0.75rem',
                        borderRadius: 4, cursor: 'pointer',
                        fontSize: '0.8rem' }}>
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}



export function MaintenanceDetail({ requestId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [manualBookOpen, setManualBookOpen] = useState(false);
  // Message 823 — when the create-form YES route hits an override-cancel
  // or a step-2 failure, it primes sessionStorage with the slot the
  // operator already chose and navigates here. We consume the payload
  // ONCE on mount and pass it to ManualBookingModal as initialValues.
  const [primedManualBookingValues, setPrimedManualBookingValues] =
    useState(() => {
      try {
        const key = `maintPrimedManualBooking_${requestId}`;
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        sessionStorage.removeItem(key);
        return JSON.parse(raw);
      } catch { return null; }
    });

  // Message 823 — if the create-form YES route primed us with a slot,
  // auto-open the manual-booking modal on mount so the operator lands
  // straight into "finish booking" with their inputs preserved.
  useEffect(() => {
    if (primedManualBookingValues) setManualBookOpen(true);
    // Intentionally only runs once on mount (values were consumed from
    // sessionStorage in the state initialiser).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const STATUS_BUTTON_LABEL = {
    open: 'Save Changes',
    acknowledged: 'Save and Mark Acknowledged',
    contractor_assigned: 'Save and Assign Contractor',
    in_progress: 'Save and Mark In Progress',
    completed: 'Save and Mark Complete',
    closed: 'Save and Mark Closed',
  };

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintenance/requests/${requestId}`, { headers: authOnly });
        if (res.ok) {
          const text = await res.text();
          const d = text ? JSON.parse(text) : {};
          setData(d);
          setForm({
            contractor_name: d.contractor_name || '',
            contractor_phone: d.contractor_phone || '',
            scheduled_date: d.scheduled_date || '',
            agent_notes: d.agent_notes || '',
            priority: d.priority || '',
            status: d.status || '',
          });
        }
      } catch {} finally { setLoading(false); }
    };
    if (requestId) fetch_();
  }, [requestId]);

  // Single atomic save: contractor, phone, scheduled date, notes, priority
  // AND status all go out in one PUT so nothing gets lost between a
  // "save details" and a "change status" step.
  const handleCompleteUpdate = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE_URL}/api/maintenance/requests/${requestId}`, {
        method: 'PUT', headers,
        body: JSON.stringify(form),
      });
      // Refresh the dashboard alert panel instantly (maintenance alerts
      // auto-dismiss server-side when status leaves 'open').
      window.dispatchEvent(new Event('eiq-alerts-refresh'));
      if (onUpdated) onUpdated();
    } catch {} finally { setSaving(false); }
  };

  if (loading) return <div className="maint-detail-overlay"><div className="maint-detail-panel"><p>Loading...</p></div></div>;
  if (!data) return null;

  const pc = PRIORITY_COLORS[data.priority] || PRIORITY_COLORS.low;
  const buttonLabel = STATUS_BUTTON_LABEL[form.status] || 'Save Changes';

  return (
    <div className="maint-detail-overlay" data-testid="maintenance-detail">
      <div className="maint-detail-panel">
        <div className="md-header">
          <h3>Maintenance Detail</h3>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>
        <div className="md-body">
          <div className="md-top-info">
            <span className="md-priority" style={{ background: pc.bg, color: pc.color }}>{pc.label}</span>
            <span className="md-category">{CATEGORY_ICONS[data.category] || ''} {data.category}</span>
            <span className="md-status">{data.status}</span>
            <span className="md-days">{data.days_open || 0} days open</span>
          </div>
          <div className="md-info-row">
            <div><strong>Property:</strong> {data.property_address}</div>
            <div><strong>Tenant:</strong> {data.tenant_name}</div>
            <div><strong>Reported via:</strong> {data.reported_via}</div>
            <div><strong>Created:</strong> {formatDate(data.created_at, '')}</div>
          </div>

          {/* 2026-08-08 — Merge Undo. When this MR was merged into
              another (via the split-alert one-click merge), show
              a banner with a one-click un-merge action so an
              accidental merge is reversible. */}
          {data.merged_into_mr_ref && (
            <div data-testid="md-merge-banner"
                 style={{ margin: '0.5rem 0',
                          padding: '0.75rem 1rem',
                          borderRadius: 8,
                          background: '#fff7ed',
                          border: '1px solid #fdba74',
                          fontSize: '0.85rem',
                          display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <strong>Merged into:</strong>{' '}
                <span data-testid="md-merged-into-ref">
                  {data.merged_into_mr_ref}
                </span>
                <span style={{ marginLeft: 8, color: '#6b7280' }}>
                  ({data.merged_at ? new Date(data.merged_at).toLocaleString() : 'unknown time'})
                </span>
              </div>
              <button
                className="btn-secondary"
                data-testid="md-unmerge-btn"
                onClick={async () => {
                  if (!window.confirm(
                    `Un-merge from ${data.merged_into_mr_ref}? This restores this MR and removes the merge link on both sides.`,
                  )) return;
                  const res = await fetch(
                    `${API_BASE_URL}/api/maintenance/requests/${data.id}/unmerge`,
                    { method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({}) },
                  );
                  if (res.ok) {
                    if (onUpdated) onUpdated();
                    if (onClose) onClose();
                  } else {
                    alert('Un-merge failed: ' + await res.text());
                  }
                }}>
                Un-merge
              </button>
            </div>
          )}

          {/* 2026-08-08 — Operator halt/unhalt.
              A halted MR has `outbound_blocked=true` and is
              archived. All senders check `_mr_still_sendable` /
              `_mr_live` which filter halted MRs, so no code path
              can send an email against it until un-halted. Use
              this when an MR is looping (see MR-V2-3B5185
              triple-ack investigation, 2026-08-08). */}
          <div data-testid="md-halt-controls"
               style={{ margin: '0.5rem 0',
                        padding: '0.75rem 1rem',
                        borderRadius: 8,
                        background: data.outbound_blocked ? '#fee2e2' : '#f9fafb',
                        border: `1px solid ${data.outbound_blocked ? '#fca5a5' : '#e5e7eb'}`,
                        fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <strong>Outbound emails:</strong>{' '}
              {data.outbound_blocked ? (
                <span style={{ color: '#991b1b', fontWeight: 700 }}>
                  BLOCKED
                  {data.outbound_blocked_reason && (
                    <span style={{ marginLeft: 8, fontWeight: 400 }}>
                      ({data.outbound_blocked_reason})
                    </span>
                  )}
                </span>
              ) : (
                <span style={{ color: '#059669' }}>Live</span>
              )}
            </div>
            {data.outbound_blocked ? (
              <button
                className="btn-primary"
                data-testid="md-unhalt-btn"
                onClick={async () => {
                  if (!window.confirm('Un-halt this MR? Outbound emails will resume.')) return;
                  const res = await fetch(
                    `${API_BASE_URL}/api/maintenance/requests/${data.id}/unhalt`,
                    { method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({}) },
                  );
                  if (res.ok) {
                    if (onUpdated) onUpdated();
                    if (onClose) onClose();
                  } else {
                    alert('Un-halt failed: ' + await res.text());
                  }
                }}>
                Un-halt & restore
              </button>
            ) : (
              <button
                className="btn-secondary"
                data-testid="md-halt-btn"
                style={{ color: '#991b1b', borderColor: '#fca5a5' }}
                onClick={async () => {
                  const reason = window.prompt(
                    'Halt reason (short — will be stored on the MR):',
                    'operator_halt_via_ui',
                  );
                  if (!reason) return;
                  const res = await fetch(
                    `${API_BASE_URL}/api/maintenance/requests/${data.id}/halt`,
                    { method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ reason }) },
                  );
                  if (res.ok) {
                    if (onUpdated) onUpdated();
                    if (onClose) onClose();
                  } else {
                    alert('Halt failed: ' + await res.text());
                  }
                }}>
                Halt outbound
              </button>
            )}
          </div>
          <div className="md-description">
            <strong>Description:</strong>
            <p style={{ whiteSpace: 'pre-wrap' }}>{decodeText(data.description)}</p>
          </div>

          {/* 2026-02 (iter 9) — Auto-booking result. When
              `_atomic_assign_and_book` completes it writes the
              scheduled slot + contractor name/email back on the MR.
              Show it prominently so agents don't have to open emails
              to know the appointment. */}
          {data.coord_assigned_outreach_id && (
            <div data-testid="md-booking-confirmed"
                 style={{ margin: '0.75rem 0', padding: '0.75rem 1rem',
                          background: '#eff6ff', border: '1px solid #93c5fd',
                          borderRadius: 8, color: '#1e3a8a', fontSize: '0.9rem' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                📅 Visit booked with contractor
              </div>
              <div>
                <strong>Contractor:</strong> {data.contractor_name || '—'}
                {data.contractor_email && (
                  <span style={{ color: '#1d4ed8', marginLeft: 6 }}>
                    ({data.contractor_email})
                  </span>
                )}
              </div>
              {(data.scheduled_start || data.scheduled_end) && (
                <div>
                  <strong>When:</strong>{' '}
                  {data.scheduled_start
                    ? new Date(data.scheduled_start).toLocaleString('en-GB', {
                        weekday: 'short', day: 'numeric', month: 'short',
                        hour: '2-digit', minute: '2-digit',
                      })
                    : '—'}
                  {data.scheduled_end && (
                    <>
                      {' – '}
                      {new Date(data.scheduled_end).toLocaleTimeString('en-GB', {
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 2026-02 (Message 811) — Manual booking control. Available
              at any stage where a booking is possible; hidden only for
              cancelled/closed/archived MRs. Records off-system agreements. */}
          {!['cancelled', 'closed', 'archived'].includes(data.status) && (
            <div style={{ margin: '1rem 0' }}>
              <button
                onClick={() => setManualBookOpen(true)}
                data-testid="md-manual-book-btn"
                style={{ background: '#004080', color: '#fff',
                          padding: '.5rem 1rem', border: 'none',
                          borderRadius: 4, cursor: 'pointer' }}
              >
                Set booking manually
              </button>
              <span style={{ marginLeft: '.75rem', color: '#666',
                              fontSize: '.85em' }}>
                Records a booking agreed off-system — sends the standard
                confirmations to both parties.
              </span>
            </div>
          )}
          {manualBookOpen && (
            <ManualBookingModal
              mrId={requestId}
              outreachId={data.coord_assigned_outreach_id
                          || data.contractor_outreach_id}
              apiKey={localStorage.getItem('apiKey') || ''}
              initialValues={primedManualBookingValues || undefined}
              onClose={() => {
                setManualBookOpen(false);
                setPrimedManualBookingValues(null);
              }}
              onBooked={() => {
                setManualBookOpen(false);
                setPrimedManualBookingValues(null);
                onUpdated && onUpdated();
              }}
            />
          )}

          {/* 2026-02 (iter 17, Issue 3) — Pending messages section:
              inbound messages the coordinator flagged for human
              review before forwarding. Deep-linked from the
              dashboard alert "open card to review and forward". */}
          <PendingMessagesSection requestId={requestId} />

          {/* 2026-02 (iter 9) — Full conversation thread. */}
          <MaintenanceThread requestId={requestId} headers={headers} />

          {/* 2026-06-04 (v2) — Routing decision + agent override. */}
          {(data.routed_to || data.routing_reason) && (
            <RoutingPanel data={data} onChange={onUpdated} />
          )}

          {/* Photo gallery */}
          {data.photo_urls && data.photo_urls.length > 0 && (
            <div className="md-photos" data-testid="md-photos">
              <strong>Photos:</strong>
              <div className="md-photo-grid">
                {data.photo_urls.map((url, i) => (
                  <img key={i} src={`${API_BASE_URL}${url}`} alt={`Photo ${i+1}`} className="md-photo" />
                ))}
              </div>
            </div>
          )}

          <div className="md-form">
            <h4>Contractor Assignment</h4>
            <div className="mf-row">
              <div className="mf-field">
                <label>Contractor Name</label>
                <input type="text" value={form.contractor_name} onChange={e => setForm(f => ({ ...f, contractor_name: e.target.value }))} data-testid="md-contractor-name" />
              </div>
              <div className="mf-field">
                <label>Phone</label>
                <input type="text" value={form.contractor_phone} onChange={e => setForm(f => ({ ...f, contractor_phone: e.target.value }))} data-testid="md-contractor-phone" />
              </div>
              <div className="mf-field">
                <label>Scheduled Date</label>
                <input type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} data-testid="md-scheduled-date" />
                {data && data.coord_proposed_visit_text && (
                  <div data-testid="md-agreed-slot"
                       style={{ marginTop: 6, fontSize: '0.78rem',
                                color: '#475569', background: '#eef2ff',
                                border: '1px solid #c7d2fe', borderRadius: 6,
                                padding: '0.35rem 0.55rem' }}>
                    Agreed slot: <strong>{data.coord_proposed_visit_text}</strong>
                  </div>
                )}
              </div>
            </div>
            <div className="mf-row">
              <div className="mf-field">
                <label>Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} data-testid="md-priority">
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="mf-field">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} data-testid="md-status">
                  {STATUS_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mf-field">
              <label>Agent Notes</label>
              <textarea value={form.agent_notes} onChange={e => setForm(f => ({ ...f, agent_notes: e.target.value }))} rows={3} data-testid="md-agent-notes" />
            </div>
            <CompletionLine data={data} />
          </div>
        </div>
        <div className="md-footer">
          <button className="btn-secondary" onClick={onClose} data-testid="btn-cancel-maintenance-detail">Cancel</button>
          <button
            className="btn-approve md-complete-btn"
            onClick={handleCompleteUpdate}
            disabled={saving}
            style={{marginLeft:'auto'}}
            data-testid="btn-complete-maintenance-update"
          >
            {saving ? 'Saving...' : buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * 2026-02 — v2 Stage 5 action buttons on the maintenance card.
 *
 * Rules (per spec):
 *   • Renders ONLY for MRs with coord_version === 'v2'. v1 cards
 *     are unaffected.
 *   • "Approve Quote" shows when a quote has arrived (coord_state
 *     is contractor_assigned AND the MR has a coord_quote_amount_gbp
 *     or coord_quote_received_at field). Clicking moves the card to
 *     In Progress.
 *   • "Mark Complete" shows when the MR is In Progress. Clicking
 *     moves it to Completed.
 *   • All requests use the same session-cookie auth as the rest of
 *     the maintenance UI.
 */
function V2StageButtons({ request: r, onUpdated, onOpenDraft }) {
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [showNote, setShowNote] = React.useState(false);
  const [error, setError] = React.useState('');
  // 2026-02 (post-deploy batch #3) — After a Quote Approve, poll
  // pending-messages for this MR. If a draft was queued, morph the
  // approve action into a "draft ready to send" pill so the operator
  // is taken to the next required step rather than assuming the
  // action completed silently.
  const [pendingDraftCount, setPendingDraftCount] = React.useState(0);
  const status = r.coord_state || r.status;

  const refreshPendingCount = React.useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/maintenance/${r.id}/pending-messages`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        // Post-deploy batch review: don't silently swallow a
        // failing read — the pill is the ONLY board-level signal
        // that a draft is queued, so a transient 500 would leave
        // the operator with no indication of the required step.
        let detail = '';
        try { detail = (await res.text()).slice(0, 400); }
        catch (_e) { /* non-fatal */ }
        console.error(
          '[V2StageButtons] pending-messages fetch failed',
          res.status, res.statusText, detail,
        );
        return;
      }
      const d = await res.json();
      const n = ((d && d.items) || [])
        .filter(m => m.status === 'pending').length;
      setPendingDraftCount(n);
    } catch (e) {
      console.error(
        '[V2StageButtons] pending-messages fetch threw', e,
      );
    }
  }, [r.id]);

  // Mount + whenever the underlying request row changes — keep the
  // pill state fresh across board refreshes.
  React.useEffect(() => { refreshPendingCount(); },
                   [refreshPendingCount,
                    r.coord_quote_approved_at,
                    r.coord_quote_rejected_at]);

  const call = async (action, payload) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/maintenance-v2/${r.id}/${action}`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(payload || {}),
        },
      );
      if (!res.ok) {
        const txt = await res.text();
        setError(`Failed: ${res.status} ${txt.slice(0, 100)}`);
        return;
      }
      const data = await res.json();
      // 2026-02 (post-deploy batch #2) — Optimistic patch: the
      // server has recorded the action; without this the local
      // `r` prop keeps `coord_quote_approved_at` undefined until
      // a full board refresh, so `canApproveQuote` stays true and
      // the buttons don't disappear until the operator reloads.
      const nowIso = new Date().toISOString();
      const patch = {};
      if (action === 'approve-quote') {
        patch.coord_quote_approved_at = nowIso;
        // 2026-02 (Approve-sends-directly UX — iter73 gap fix)
        // Merge the sent-outcome fields the server put on the
        // response so the "Sent to {email} at {time}" line
        // renders without a page reload. Only set if the
        // server actually stamped them — a failed send leaves
        // them null, and the pill fallback picks up the draft
        // that remained pending.
        if (data.coord_quote_approved_email_sent_at) {
          patch.coord_quote_approved_email_sent_at = (
            data.coord_quote_approved_email_sent_at
          );
        }
        if (data.coord_quote_approved_email_to) {
          patch.coord_quote_approved_email_to = (
            data.coord_quote_approved_email_to
          );
        }
      } else if (action === 'reject-quote') {
        patch.coord_quote_rejected_at = nowIso;
      } else if (action === 'confirm-completion') {
        patch.job_complete_confirmed_at = nowIso;
      }
      if (onUpdated) onUpdated(data.status, patch);
      window.dispatchEvent(new Event('eiq-alerts-refresh'));
      setShowNote(false);
      setNote('');
      // Refresh the pending count after any action — the backend
      // may have queued a draft as part of this action's follow-up.
      refreshPendingCount();
    } catch (e) {
      setError(`Failed: ${String(e).slice(0, 100)}`);
    } finally {
      setBusy(false);
    }
  };

  const hasQuote = Boolean(
    r.coord_quote_amount_gbp ||
    r.coord_quote_received_at ||
    r.coord_quote_summary,
  );
  // 2026-02 (Support finding, MR-V2-4F8265) — the quote is a
  // PROPERTY of the job, not a phase of it. Approve/Reject must
  // be reachable whenever there's an unresolved quote, at ANY
  // state after `contractor_assigned` (including `date_proposed`,
  // `re_negotiating`, `in_progress`). Prior gate `status ===
  // 'contractor_assigned'` was too narrow — a quote arriving
  // after the job had already progressed to `date_proposed`
  // meant the operator couldn't approve it at all.
  //
  // Guard is now: quote exists AND not already resolved.
  // Symmetrical to the badge logic on L1746.
  const canApproveQuote = hasQuote
    && !r.coord_quote_approved_at
    && !r.coord_quote_rejected_at;
  // 2026-08-06 (Kanban Part 2c) — Mark Complete is available on any
  // post-booking stage so the manual path (contractor phoned, no
  // email) is always one click. Previously locked to `in_progress`
  // only, which meant a card sitting at `contractor_assigned`
  // without a quote had no visible complete action.
  const canMarkComplete = (
    status === 'in_progress'
    || status === 'contractor_assigned'
    || status === 're_negotiating'
  );
  // 2026-08-06 (Kanban Part 2b) — Confirm-completion prompt when
  // the AI classified the contractor's inbound as "job_complete".
  const needsCompletionConfirm = Boolean(
    r.job_complete_reported_at && !r.job_complete_confirmed_at
    && status !== 'completed',
  );

  // 2026-02 (post-deploy batch #3) — Pending-draft pill takes the
  // place of the (now-hidden) Approve button after a Quote Approve
  // that queued a draft. Without this the operator sees the
  // approved-badge and assumes the job is done, but the follow-up
  // email is silently sitting in the pending queue.
  //
  // 2026-02 (pill morph extension) — Broadened to show for ANY
  // queue-creating action, not only quote resolution. All queue
  // sites (11 in maintenance_coord_v2.py) go through the single
  // `_add_pending_forward_message` choke, so `pendingDraftCount`
  // covers contractor decline forwards, dead-end escalations,
  // reneg-related drafts, tenant-message drafts, and more — the
  // whole class of "did that action actually finish?" reports the
  // pill kills gets closed here.
  const showPendingDraftPill = pendingDraftCount > 0;
  const pendingPillLabel = (() => {
    if (r.coord_quote_approved_at) return 'Approved — draft ready to send';
    if (r.coord_quote_rejected_at) return 'Rejected — draft ready to send';
    // Neutral case: contractor decline forward, dead-end
    // escalation, tenant-side message, etc. Keep the wording
    // action-first ("Draft ready to send") so the pill reads the
    // same whatever queued it — operator still knows to click.
    return 'Draft ready to send';
  })();

  const complianceRejected = r.is_compliance === true && Boolean(r.coord_quote_rejected_at);
  const pricedComplianceQuotes = r.is_compliance === true
    ? (r.compliance_quotes || []).filter((q) => q.amount_gbp != null) : [];
  const multiQuote = canApproveQuote && pricedComplianceQuotes.length > 1;

  if (!canApproveQuote && !canMarkComplete && !needsCompletionConfirm
      && !showPendingDraftPill && !complianceRejected) return null;

  return (
    <div
      data-testid={`v2-stage-buttons-${r.id}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        marginTop: 8, paddingTop: 8,
        borderTop: '1px solid #e2e8f0',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      {complianceRejected && <ComplianceRejectedNotice r={r} />}
      {r.coord_quote_amount_gbp && !r.coord_quote_approved_at && !r.coord_quote_rejected_at && (
        <div
          data-testid={`v2-quote-badge-${r.id}`}
          style={{
            fontSize: '0.72rem',
            background: '#fef3c7', border: '1px solid #fbbf24',
            borderRadius: 6, padding: '6px 10px',
            color: '#78350f',
          }}
        >
          <strong>Quote pending: £{r.coord_quote_amount_gbp}</strong>
          {r.coord_quote_summary && (
            <span style={{ color: '#475569' }}> — {r.coord_quote_summary}</span>
          )}
        </div>
      )}
      {r.coord_quote_approved_at && r.coord_quote_amount_gbp && (
        <div
          data-testid={`v2-quote-approved-${r.id}`}
          style={{
            fontSize: '0.72rem',
            background: '#ecfdf5', border: '1px solid #10b981',
            borderRadius: 6, padding: '6px 10px', color: '#065f46',
            display: 'flex', flexDirection: 'column', gap: 2,
          }}
        >
          <strong>Quote approved: £{r.coord_quote_amount_gbp}</strong>
          {/* 2026-02 (Approve-sends-directly UX) — Visible
              confirmation that the contractor was actually
              emailed. Renders "Sent to sam@example.com at
              14:23" when the auto-send fired successfully.
              Absent when the send failed, prompting the
              operator to check the pending pill fallback
              (which stays visible in that case because
              suppression only runs on successful send). */}
          {r.coord_quote_approved_email_sent_at
            && r.coord_quote_approved_email_to && (
            <span
              data-testid={`v2-quote-approved-sent-${r.id}`}
              style={{
                fontSize: '0.68rem', color: '#047857',
                fontWeight: 500,
              }}
            >
              Sent to {r.coord_quote_approved_email_to} at{' '}
              {(() => {
                try {
                  const d = new Date(
                    r.coord_quote_approved_email_sent_at,
                  );
                  return d.toLocaleTimeString([], {
                    hour: '2-digit', minute: '2-digit',
                  });
                } catch (_e) {
                  return r.coord_quote_approved_email_sent_at;
                }
              })()}
            </span>
          )}
        </div>
      )}
      {showPendingDraftPill && (
        <button
          type="button"
          data-testid={`v2-pending-draft-pill-${r.id}`}
          onClick={() => { if (onOpenDraft) onOpenDraft(r.id); }}
          style={{
            fontSize: '0.75rem',
            background: '#fffbeb', border: '1px solid #f59e0b',
            borderRadius: 6, padding: '6px 10px',
            color: '#78350f',
            textAlign: 'left', cursor: 'pointer', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span aria-hidden="true">✎</span>
          <span>
            {pendingPillLabel}
            {pendingDraftCount > 1 ? ` (${pendingDraftCount})` : ''}
          </span>
          <span style={{ marginLeft: 'auto', color: '#b45309',
                          fontWeight: 400, fontSize: '0.7rem' }}>
            Click to review
          </span>
        </button>
      )}
      {r.status === 'contractor_assigned'
        && r.coord_quote_amount_gbp
        && !r.coord_quote_approved_at
        && !r.coord_quote_rejected_at && (
        <div
          data-testid={`v2-quote-warning-${r.id}`}
          style={{ fontSize: '0.68rem', color: '#b45309' }}
        >
          Work scheduled with unapproved cost.
        </div>
      )}
      {showNote && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (who pays, insurance ref, etc)"
          data-testid={`v2-stage-note-${r.id}`}
          style={{
            width: '100%', padding: 6, fontSize: '0.75rem',
            border: '1px solid #cbd5e1', borderRadius: 4,
            resize: 'vertical', minHeight: 40,
          }}
        />
      )}
      {needsCompletionConfirm && (
        <div
          data-testid={`v2-job-complete-prompt-${r.id}`}
          style={{
            background: '#fef3c7', border: '1px solid #fbbf24',
            borderRadius: 6, padding: '6px 10px',
            fontSize: '0.72rem', color: '#78350f',
          }}
        >
          <strong>Contractor says the job is complete.</strong>
          {r.job_complete_snippet && (
            <div style={{ marginTop: 2, color: '#0f172a',
                          fontStyle: 'italic', fontSize: '0.68rem' }}>
              “{(r.job_complete_snippet || '').slice(0, 140)}…”
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {needsCompletionConfirm && (
          <button
            type="button"
            disabled={busy}
            data-testid={`v2-confirm-completion-${r.id}`}
            onClick={() => call('confirm-completion', {})}
            style={{
              background: '#b45309', color: '#fff', border: 'none',
              padding: '6px 10px', borderRadius: 4, fontSize: '0.75rem',
              fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Confirming…' : 'Confirm job complete'}
          </button>
        )}
        {multiQuote && (
          <ComplianceQuotePicker
            r={r} quotes={pricedComplianceQuotes} busy={busy}
            onApprove={(oid) => call('approve-quote', { note: note || undefined, outreach_id: oid })}
          />
        )}
        {canApproveQuote && (
          <>
            {!multiQuote && <button
              type="button"
              disabled={busy}
              data-testid={`v2-approve-quote-${r.id}`}
              onClick={() => {
                // 2026-02 (Round 1 UX fix, user-approved) —
                // SINGLE-CLICK. Fires immediately. `busy` state
                // provides in-flight feedback; the card refresh
                // after success removes the buttons so the user
                // sees the state change without a confirm step.
                call('approve-quote', { note: note || undefined });
              }}
              style={{
                background: busy ? '#a7f3d0' : '#059669',
                color: '#fff', border: 'none',
                padding: '8px 14px', borderRadius: 4,
                fontSize: '0.8rem', fontWeight: 700,
                cursor: busy ? 'wait' : 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              }}
            >
              {busy ? 'Approving…' : '✓ Approve Quote'}
            </button>}
            {/* Visual gap — an accidental Reject after an
                Approve is worse than an accidental Approve.
                Separator + margin-left keeps them physically
                distinct. */}
            <span style={{
              display: 'inline-block', width: 24,
            }} />
            <button
              type="button"
              disabled={busy}
              data-testid={`v2-reject-quote-${r.id}`}
              onClick={() => {
                call('reject-quote', { reason: note || undefined });
              }}
              style={{
                background: '#fff',
                color: busy ? '#fca5a5' : '#b91c1c',
                border: `1px solid ${busy ? '#fca5a5' : '#b91c1c'}`,
                padding: '8px 14px', borderRadius: 4,
                fontSize: '0.8rem', fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              {busy ? 'Rejecting…' : 'Reject'}
            </button>
            {/* Optional note — post-hoc capture, NOT gating. */}
            <details style={{
              display: 'inline-block', marginLeft: 12,
              fontSize: '0.75rem',
            }}>
              <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                Add note (optional)
              </summary>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Approval note / rejection reason"
                style={{
                  display: 'block', marginTop: 4, width: 260,
                  padding: 4, fontSize: '0.75rem',
                }}
              />
            </details>
          </>
        )}
        {canMarkComplete && (
          <button
            type="button"
            disabled={busy}
            data-testid={`v2-mark-complete-${r.id}`}
            onClick={() => {
              if (!showNote) { setShowNote(true); return; }
              call('mark-complete', { note: note || undefined });
            }}
            style={{
              background: '#0f172a', color: '#fff', border: 'none',
              padding: '6px 10px', borderRadius: 4, fontSize: '0.75rem',
              fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Working…' : (showNote ? 'Confirm complete → Completed' : 'Mark Complete')}
          </button>
        )}
        {showNote && (
          <button
            type="button"
            disabled={busy}
            data-testid={`v2-cancel-${r.id}`}
            onClick={() => { setShowNote(false); setNote(''); }}
            style={{
              background: '#fff', color: '#475569',
              border: '1px solid #cbd5e1',
              padding: '6px 10px', borderRadius: 4, fontSize: '0.75rem',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
      </div>
      {error && (
        <div
          data-testid={`v2-stage-error-${r.id}`}
          style={{ color: '#dc2626', fontSize: '0.72rem' }}
        >{error}</div>
      )}
    </div>
  );
}

